import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';

import { useActivateMembershipType } from 'src/features/membershipType/api/useActivateMembershipType';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';
import { MembershipType } from 'src/features/membershipType/types';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { ICON_MAP } from 'src/utils/constants';
import { useFormatCustomError } from 'src/utils/errors';
import { generalErrorMessages } from 'src/utils/messages';

interface ActivateMembershipTemplateProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTemplate?: MembershipType;
    children?: React.ReactNode;
}

function ActivateMembershipType<TData>({ selectedTemplate, children }: ActivateMembershipTemplateProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const formatCustomError = useFormatCustomError();
    const { mutate: activateMembershipType } = useActivateMembershipType({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });

            const templateName = success.translations?.find((membership) => membership.language === locale)?.name;

            toast({
                title: formatMessage(membershipTypeMessages.activatedTemplateSuccess),
                description: formatMessage(membershipTypeMessages.activatedTemplateSuccessDescription, {
                    entry: templateName,
                }),
            });
        },
        onError: (error) => {
            const errorDetails = error?.response?.data?.errors[0];
            const title = formatMessage(generalErrorMessages.somethingWentWrong);
            const description = errorDetails
                ? formatCustomError(errorDetails)
                : formatMessage(generalErrorMessages.unknownError);

            toast({
                title,
                description,
                variant: 'destructive',
            });
        },
    });
    if (!selectedTemplate) return null;

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...membershipTypeMessages.activateTemplateModal },
                subTitle: {
                    ...membershipTypeMessages.activateTemplateSubtitle,
                    values: {
                        entry: selectedTemplate.translations?.find((temp) => temp.language === locale)?.name ?? '-',
                    },
                },
                icon: ICON_MAP.WAVE_PULSE as IconProp,
                confirmationMessage: {
                    ...membershipTypeMessages.confirmActivateTemplate,
                    values: {
                        entry: selectedTemplate.translations?.find((temp) => temp.language === locale)?.name ?? '-',
                    },
                },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: membershipTypeMessages.activateTemplate,
                callbackFn: () => {
                    if (selectedTemplate) {
                        return (
                            activateMembershipType({ parkingId, typeId: selectedTemplate.id || '' }),
                            setIsModalOpen(false)
                        );
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default ActivateMembershipType;
